import type { ModuleRecord, ModuleSection, ModuleSource } from "../types";

function textFromElement(element: Element | Document): string {
  return (element.textContent ?? "").replace(/\s+/g, " ").trim();
}

function sectionTitle(section: Element): string {
  const heading = section.querySelector("h2, h3, summary");
  return heading ? textFromElement(heading) : section.id;
}

function extractSections(document: Document): ModuleSection[] {
  const sections = Array.from(document.querySelectorAll("section[id]"));
  return sections.map((section) => ({
    id: section.id,
    title: sectionTitle(section),
    text: textFromElement(section)
  }));
}

function recordFromHtml(source: ModuleSource, html: string): ModuleRecord {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  return {
    ...source,
    sections: extractSections(parsed),
    plainText: textFromElement(parsed)
  };
}

// Records BASE — rápido, SEM parsing de HTML. Todos os módulos são lazy (html === ""),
// então começam com sections vazio e plainText = título. É o que renderiza no primeiro
// paint; a busca completa é enriquecida em background por buildSearchIndex.
export function createModuleRecords(sources: ModuleSource[]): ModuleRecord[] {
  return sources.map((source) => {
    if (source.html) {
      // Caminho eager legado (nenhum módulo usa mais, mas mantém compat).
      return recordFromHtml(source, source.html);
    }
    return { ...source, sections: [], plainText: source.title };
  });
}

// Enriquece os records com o texto para busca, carregando os HTMLs sob demanda e
// fazendo o DOMParser fora do caminho crítico. Deve ser chamado em background (idle)
// após o primeiro paint — NUNCA no startup síncrono (era o que travava o carregamento).
export async function buildSearchIndex(sources: ModuleSource[]): Promise<ModuleRecord[]> {
  return Promise.all(
    sources.map(async (source) => {
      try {
        const html = source.html || (source.loadHtml ? await source.loadHtml() : "");
        if (!html) {
          return { ...source, sections: [], plainText: source.title };
        }
        return recordFromHtml(source, html);
      } catch {
        // Falha de chunk não pode quebrar a busca — degrada para só o título.
        return { ...source, sections: [], plainText: source.title };
      }
    })
  );
}
